import React, { useState } from 'react';
import { FormGroup, Label, Input, Col, Row } from 'reactstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../helpers/datePickerConfig';
import { Search, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, CheckSquare, Square } from 'react-feather';
import PropTypes from 'prop-types';
import { formatDate } from '../../helpers/dateUtils';
import Table from '../ui/Table';
import facturasApiService from '../../services/http/facturasApiService';
import logger from '../../services/logger';
import colors from '../../theme/colors';

const CheckboxClienteCell = ({ original, selectedClientes, onCheckboxChange }) => (
  <Input
    type="checkbox"
    checked={selectedClientes.includes(original.ccodcli)}
    onChange={() => onCheckboxChange(original.ccodcli, original.suma_importes)}
  />
);
CheckboxClienteCell.propTypes = {
  original: PropTypes.shape({
    ccodcli: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    suma_importes: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  selectedClientes: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  onCheckboxChange: PropTypes.func.isRequired,
};

const FiltrosFacturación = ({
  headquarter,
  selectedClientes,
  setSelectedClientes,
  next,
  ret,
  formaPago,
  setFormaPago,
  tipoUsuario,
  setTipoUsuario,
  fechaFin,
  setFechaFin,
  fechaInicio,
  setFechaInicio,
  euros,
  setSelectedImports,
  totalClientesSeleccionados,
}) => {
  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);
  const [filtrados, setFiltrados] = useState([]);
  const [todosSeleccionados, setTodosSeleccionados] = useState(false);

  const handleTipoUsuarioChange = (e) => setTipoUsuario(e.target.value);
  const handleFormaPagoChange = (e) => setFormaPago(e.target.value);

  const toggleSeleccionarTodos = () => {
    if (todosSeleccionados) {
      setSelectedClientes([]);
      setSelectedImports([]);
      setTodosSeleccionados(false);
    } else {
      const todos = filtrados.map((item) => item.ccodcli);
      const imports = filtrados.map((item) => ({ id: item.ccodcli, value: item.suma_importes }));
      setSelectedClientes(todos);
      setSelectedImports(imports);
      setTodosSeleccionados(true);
    }
  };

  const handleBuscar = async () => {
    setLoading(true);
    const filtros = {
      headquarter,
      tipoUsuario,
      fechaInicio: fechaInicio ? formatDate(fechaInicio, 'yyyy-MM-dd') : null,
      fechaFin: fechaFin ? formatDate(fechaFin, 'yyyy-MM-dd') : null,
      formaPago,
    };
    try {
      const filtered = await facturasApiService.filtrarContabilidad(filtros);
      setFiltrados(filtered);
      setSelectedClientes([]);
    } catch (error) {
      logger.error('Error al buscar:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (ccodcli, sumaImportes) => {
    setSelectedClientes((prev) =>
      prev.includes(ccodcli) ? prev.filter((id) => id !== ccodcli) : [...prev, ccodcli],
    );
    setSelectedImports((prev) =>
      selectedClientes.includes(ccodcli)
        ? prev.filter((importe) => importe.id !== ccodcli)
        : [...prev, { id: ccodcli, value: sumaImportes }],
    );
  };

  const toolbarButtons = (
    <>
      <button
        type="button"
        onClick={handleBuscar}
        disabled={loading}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 14px',
          fontSize: '0.75rem', fontWeight: 600, borderRadius: '6px', border: 'none',
          backgroundColor: colors.primary, color: '#fff',
          cursor: loading ? 'not-allowed' : 'pointer',
          opacity: loading ? 0.5 : 1, transition: 'all 0.15s',
        }}
      >
        <Search size={13} />
        {loading ? 'Buscando...' : 'Buscar'}
      </button>
      {filtrados.length > 0 && (
        <button
          type="button"
          onClick={toggleSeleccionarTodos}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 12px',
            fontSize: '0.75rem', fontWeight: 600, borderRadius: '6px',
            border: '1px solid #e5e7eb', backgroundColor: '#fff', color: '#6b7280',
            cursor: 'pointer', transition: 'all 0.15s',
          }}
        >
          {todosSeleccionados ? <Square size={13} /> : <CheckSquare size={13} />}
          {todosSeleccionados ? 'Deseleccionar todos' : 'Seleccionar todos'}
        </button>
      )}
      <button
        type="button"
        onClick={ret}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 12px',
          fontSize: '0.75rem', fontWeight: 600, borderRadius: '6px',
          border: '1px solid #e5e7eb', backgroundColor: '#fff', color: '#374151',
          cursor: 'pointer', transition: 'all 0.15s',
        }}
      >
        <ChevronLeft size={13} />
        Atrás
      </button>
      <button
        type="button"
        onClick={next}
        disabled={filtrados.length === 0 || selectedClientes.length === 0}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 12px',
          fontSize: '0.75rem', fontWeight: 600, borderRadius: '6px',
          border: `1px solid ${colors.primary}`, backgroundColor: '#fff', color: colors.primary,
          cursor: (filtrados.length === 0 || selectedClientes.length === 0) ? 'not-allowed' : 'pointer',
          opacity: (filtrados.length === 0 || selectedClientes.length === 0) ? 0.5 : 1,
          transition: 'all 0.15s',
        }}
      >
        Siguiente
        <ChevronRight size={13} />
      </button>
    </>
  );

  return (
    <>
      <div style={{
        backgroundColor: '#fff', borderRadius: '12px',
        border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgb(0 0 0 / 0.06)',
        overflow: 'visible', marginBottom: '16px',
      }}>
        {/* Toolbar */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: showFilters ? '1px solid #f3f4f6' : 'none',
        }}>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '0.8rem', fontWeight: 600, color: '#374151',
              padding: '4px 8px', borderRadius: '6px',
            }}
          >
            <Search size={15} style={{ color: '#9ca3af' }} />
            {`Filtros facturación — ${headquarter}`}
            {showFilters
              ? <ChevronUp size={14} style={{ color: '#9ca3af' }} />
              : <ChevronDown size={14} style={{ color: '#9ca3af' }} />}
          </button>
          <div style={{ display: 'flex', gap: '8px' }}>
            {toolbarButtons}
          </div>
        </div>

        {/* Filtros colapsables */}
        {showFilters && (
          <div style={{
            padding: '16px',
            opacity: loading ? 0.5 : 1,
            pointerEvents: loading ? 'none' : 'auto',
            transition: 'opacity 0.2s',
          }}>
            <Row className="mb-3">
              <Col md={4}>
                <FormGroup tag="fieldset">
                  <Label>Tipo de Usuario</Label>
                  <FormGroup check>
                    <Label check>
                      <Input type="radio" name="tipoUsuario" value="1" checked={tipoUsuario === '1'} onChange={handleTipoUsuarioChange} />{' '}
                      Alumno
                    </Label>
                  </FormGroup>
                  <FormGroup check>
                    <Label check>
                      <Input type="radio" name="tipoUsuario" value="2" checked={tipoUsuario === '2'} onChange={handleTipoUsuarioChange} />{' '}
                      Empresa
                    </Label>
                  </FormGroup>
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Fecha Inicio</Label>
                  <DatePicker
                    selected={fechaInicio} onChange={(date) => setFechaInicio(date)}
                    dateFormat="dd/MM/yyyy" locale="es" className="form-control" wrapperClassName="w-100"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Fecha Fin</Label>
                  <DatePicker
                    selected={fechaFin} onChange={(date) => setFechaFin(date)}
                    dateFormat="dd/MM/yyyy" locale="es" className="form-control"
                    minDate={fechaInicio} wrapperClassName="w-100"
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="formaPago">Forma de Pago</Label>
                  <Input type="select" name="formaPago" id="formaPago" value={formaPago} onChange={handleFormaPagoChange}>
                    <option value="99">Todos</option>
                    <option value="0">Contado Efectivo</option>
                    <option value="1">Tarjeta Crédito</option>
                    <option value="2">Recibo Domiciliado</option>
                    <option value="3">Transferencia</option>
                    <option value="4">Talón o Cheque</option>
                    <option value="5">Financiado</option>
                    <option value="6">Stripe</option>
                    <option value="7">Pago Débito</option>
                    <option value="8">Redsys</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>
          </div>
        )}
      </div>

      {filtrados.length > 0 && (
        <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '0.8rem', color: '#374151' }}>
            <b>Clientes seleccionados: {totalClientesSeleccionados}</b>
            {euros > 0 && <span style={{ marginLeft: '12px' }}><b>Total: {euros} €</b></span>}
          </span>
        </div>
      )}

      {filtrados.length > 0 && (
        <Table
          columns={[
            {
              Header: 'Seleccionar',
              accessor: 'ccodcli',
              CellComponent: CheckboxClienteCell,
              cellProps: { selectedClientes, onCheckboxChange: handleCheckboxChange },
              sortable: false,
            },
            { Header: 'Cliente', accessor: 'cliente' },
            { Header: 'Total recibos', accessor: 'total_recibos' },
            { Header: 'Suma importes', accessor: 'suma_importes' },
          ]}
          data={filtrados}
          defaultPageSize={10}
          showPaginationBottom
          className="-striped -highlight text-center"
        />
      )}
    </>
  );
};

FiltrosFacturación.propTypes = {
  headquarter: PropTypes.string.isRequired,
  selectedClientes: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  setSelectedClientes: PropTypes.func.isRequired,
  ret: PropTypes.func.isRequired,
  next: PropTypes.func.isRequired,
  tipoUsuario: PropTypes.string,
  setTipoUsuario: PropTypes.func.isRequired,
  formaPago: PropTypes.string,
  setFormaPago: PropTypes.func.isRequired,
  fechaFin: PropTypes.instanceOf(Date),
  setFechaFin: PropTypes.func.isRequired,
  fechaInicio: PropTypes.instanceOf(Date),
  setFechaInicio: PropTypes.func.isRequired,
  euros: PropTypes.oneOfType([PropTypes.bool, PropTypes.array, PropTypes.number]),
  setSelectedImports: PropTypes.func,
  totalClientesSeleccionados: PropTypes.number,
};

export default FiltrosFacturación;
